import { Button } from '@lego/connect-components-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useUserStore } from '../../stores/user-store';
import FormPage from '../form';
import { OperatorAccessPage } from '../operator-access';

type TabKey = 'forms' | 'operator-access';

export const FormsManagementPage = () => {
  const { t } = useTranslation();
  const { canManageOperatorAccess } = useUserStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const showOperatorAccess = canManageOperatorAccess();
  const requestedTab = (searchParams.get('tab') ?? 'forms') as TabKey;
  const activeTab: TabKey =
    requestedTab === 'operator-access' && showOperatorAccess ? 'operator-access' : 'forms';

  const isEditingForm = searchParams.has('editing');

  const setTab = (tab: TabKey) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'forms') {
      next.delete('tab');
    } else {
      next.set('tab', tab);
    }
    setSearchParams(next, { replace: true });
  };

  const handleBackToForms = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('editing');
    setSearchParams(next, { replace: true });
  };

  const tabClass = (tab: TabKey) =>
    `px-300 py-150 text-body-sm font-brand font-bold border-b-25 -mb-25 transition-colors ${
      activeTab === tab
        ? 'border-core-blue-900 text-core-blue-900'
        : 'border-transparent text-core-slate-1100 hover:text-core-slate-1300'
    }`;

  const isOperatorAccessShown = activeTab === 'operator-access' && showOperatorAccess;

  return (
    <div className="flex h-full flex-col">
      {isEditingForm ? (
        <div className="sticky top-0 z-10 flex items-center px-400 py-300 bg-core-white border-b border-core-slate-100 shrink-0">
          <Button
            variant="neutral"
            appearance="ghost"
            size="small"
            iconName="chevron-left"
            onClick={handleBackToForms}
          >
            {t('formPrintPage.backToForms')}
          </Button>
        </div>
      ) : (
        <div className="px-400 pt-300 border-b-25 border-core-slate-100 flex gap-100">
          <button type="button" className={tabClass('forms')} onClick={() => setTab('forms')}>
            {t('formsManagementPage.tabs.forms')}
          </button>
          {showOperatorAccess && (
            <button
              type="button"
              className={tabClass('operator-access')}
              onClick={() => setTab('operator-access')}
            >
              {t('formsManagementPage.tabs.operatorAccess')}
            </button>
          )}
        </div>
      )}

      <div className="h-full" hidden={isOperatorAccessShown}>
        <FormPage />
      </div>
      {isOperatorAccessShown && <OperatorAccessPage />}
    </div>
  );
};

export default FormsManagementPage;
